// apps/web-pwa/src/components/AvailabilityPickerScreen.tsx
// Recipient Availability Picker — converted from
// design-samples/pwa-availability-picker.html (signed off prototype).
// Presentational component: caller owns data + submit wiring.

import { useMemo, useRef, useState } from 'react';
import organizerPhotoDefault from '../assets/availability-picker/organizer-photo.png';
import phoneMockup from '../assets/availability-picker/phone-mockup-text.png';

export type ResponseState = 'works' | 'maybe' | 'cant' | null;

export type PickerSlot = {
  id: string;
  startMs: number;
  endMs: number;
  mealtag?: string;
  initialResponse?: ResponseState;
};

export type PickerDayGroup = {
  dayId: string;
  dayLabel: string;
  slots: PickerSlot[];
};

export interface SmsConsentValue {
  consented: boolean;
}

export interface AvailabilityPickerScreenProps {
  eventTitle: string;
  organizerName: string;
  organizerPhotoUrl?: string;
  roughWindowLabel: string;
  locationLabel?: string;
  peopleCount?: number;
  peoplePileInitials?: string[];
  days: PickerDayGroup[];
  smsConsent: {
    maskedPhone: string;
    initialConsented?: boolean;
  };
  onSubmit: (payload: {
    responses: Record<string, ResponseState>;
    note?: string;
    smsConsent: SmsConsentValue;
  }) => void;
  onBack?: () => void;
  onMore?: () => void;
  onDownloadApp?: () => void;
}

const PILE_BG = ['bg-[#5B4DE0]', 'bg-[#E0795B]', 'bg-[#4FBF85]'];

function CheckIcon({ size = 11, strokeWidth = 3 }: { size?: number; strokeWidth?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function MaybeIcon({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function CantIcon({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function CalendarIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="17" rx="3" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19c1.2-3 3.4-4.4 6-4.4s4.8 1.4 6 4.4" />
      <circle cx="17" cy="9" r="2.6" />
    </svg>
  );
}

function LockIcon({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 1 1 8 0v4" />
    </svg>
  );
}

function ChatIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V6a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v9z" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="13 6 19 12 13 18" />
    </svg>
  );
}

type SlotPillProps = {
  state: 'works' | 'maybe' | 'cant';
  active: boolean;
  onClick: () => void;
  label: string;
};

function SlotPill({ state, active, onClick, label }: SlotPillProps) {
  const base =
    'inline-flex items-center gap-1 rounded-full border-[1.5px] px-2.5 py-1.5 text-[11.5px] font-semibold leading-none min-h-[32px] transition-colors';
  const inactive = 'bg-white border-[#ECEAF1] text-[#6B6880] hover:border-[#A6A3B8]';
  const activeStyles =
    state === 'works'
      ? 'bg-[#E5F5EE] border-[#1FB573] text-[#1FB573]'
      : state === 'maybe'
        ? 'bg-[#FDF1DE] border-[#E9A23B] text-[#E9A23B]'
        : 'bg-[#FCEBED] border-[#E04B5C] text-[#E04B5C]';

  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`${base} ${active ? activeStyles : inactive}`}
    >
      {active && state === 'works' && <CheckIcon />}
      {active && state === 'maybe' && <MaybeIcon />}
      {active && state === 'cant' && <CantIcon />}
      {label}
    </button>
  );
}

export function AvailabilityPickerScreen({
  eventTitle,
  organizerName,
  organizerPhotoUrl,
  roughWindowLabel,
  locationLabel,
  peopleCount,
  peoplePileInitials,
  days,
  smsConsent,
  onSubmit,
  onBack,
  onMore,
  onDownloadApp,
}: AvailabilityPickerScreenProps) {
  const [responses, setResponses] = useState<Record<string, ResponseState>>(() => {
    const map: Record<string, ResponseState> = {};
    for (const day of days) {
      for (const slot of day.slots) {
        map[slot.id] = slot.initialResponse ?? null;
      }
    }
    return map;
  });
  const [note, setNote] = useState('');
  const [consented, setConsented] = useState(smsConsent.initialConsented ?? true);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const hasAnyResponse = useMemo(
    () => Object.values(responses).some((r) => r !== null),
    [responses]
  );

  const togglePill = (slotId: string, next: 'works' | 'maybe' | 'cant') => {
    setResponses((prev) => ({
      ...prev,
      [slotId]: prev[slotId] === next ? null : next,
    }));
  };

  const handleNoteInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }
  };

  const handleSubmit = () => {
    if (!hasAnyResponse) return;
    const trimmedNote = note.trim();
    onSubmit({
      responses,
      note: trimmedNote || undefined,
      smsConsent: { consented },
    });
  };

  const initials = peoplePileInitials ?? ['M', 'J', 'S'];
  const visibleInitials = initials.slice(0, 3);
  const overflowCount =
    typeof peopleCount === 'number'
      ? Math.max(0, peopleCount - visibleInitials.length)
      : Math.max(0, initials.length - visibleInitials.length);

  return (
    <div className="min-h-screen bg-[#FAF8FB] text-[#15131C] font-sans antialiased">
      <div className="max-w-md mx-auto flex flex-col">
        {/* Top bar */}
        <div className="grid grid-cols-[40px_1fr_40px] items-center px-3.5 py-1 h-12">
          <button
            type="button"
            aria-label="Back"
            onClick={onBack}
            className="w-9 h-9 grid place-items-center rounded-xl text-[#15131C] hover:bg-[#F1EEF7] active:scale-95 transition"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="text-center text-[15px] font-bold text-[#15131C] m-0">
            {eventTitle}
          </h1>
          <button
            type="button"
            aria-label="More"
            onClick={onMore}
            className="w-9 h-9 grid place-items-center rounded-xl text-[#2C2840] hover:bg-[#F1EEF7] active:scale-95 transition text-[22px] leading-none tracking-wider"
          >
            <span aria-hidden="true">{'⋯'}</span>
          </button>
        </div>

        {/* Trust badge */}
        <div className="flex justify-center -mt-0.5 mb-1.5">
          <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[#5B4DE0] bg-[#F5F2FF] px-2.5 py-[3px] rounded-full">
            <LockIcon />
            Secure invite
          </span>
        </div>

        <div className="px-[18px] pb-6 pt-1.5">
          {/* Intro card */}
          <section
            aria-label="Event summary"
            className="bg-[#F5F2FF] border border-[#E5DFFB] rounded-[20px] p-3.5 shadow-[0_1px_2px_rgba(20,18,40,0.04)] grid grid-cols-[48px_1fr_auto] gap-x-3 items-center"
          >
            {/* Avatar wrap (LRN-20260502-003: no clip on wrapper, sibling crown outside mask) */}
            <div className="relative w-11 h-11">
              <div className="w-11 h-11 rounded-full overflow-hidden bg-[#F1EEF7]">
                <img
                  src={organizerPhotoUrl ?? organizerPhotoDefault}
                  alt=""
                  className="w-full h-full object-cover scale-[1.08]"
                  style={{ objectPosition: 'center 30%' }}
                />
              </div>
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                className="absolute -top-[5px] -right-[7px] w-[18px] h-[18px] text-[#5B4DE0]"
                style={{ filter: 'drop-shadow(0 1px 2px rgba(91,77,224,.25))' }}
              >
                <path d="M3 8l4 3 5-6 5 6 4-3-2 11H5L3 8z" />
              </svg>
            </div>

            <div className="min-w-0">
              <div className="text-[14.5px] font-bold text-[#15131C] mb-1.5">
                {organizerName} wants to catch up!
              </div>
              <div className="grid gap-1">
                <div className="flex items-start gap-1.5 text-[12.5px] text-[#6B6880] leading-tight">
                  <span className="text-[#A6A3B8] flex-shrink-0 mt-px">
                    <CalendarIcon />
                  </span>
                  <span>
                    Rough window{' '}
                    <span className="text-[#2C2840] font-semibold">
                      {roughWindowLabel}
                    </span>
                  </span>
                </div>
                {locationLabel && (
                  <div className="flex items-start gap-1.5 text-[12.5px] text-[#6B6880] leading-tight">
                    <span className="text-[#A6A3B8] flex-shrink-0 mt-px">
                      <PinIcon />
                    </span>
                    <span>{locationLabel}</span>
                  </div>
                )}
              </div>
            </div>

            <div
              className="flex flex-col items-center gap-1 self-start pt-0.5"
              aria-label={
                typeof peopleCount === 'number' ? `${peopleCount} people invited` : undefined
              }
            >
              {typeof peopleCount === 'number' && (
                <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#2C2840]">
                  <span className="text-[#6B6880]">
                    <PeopleIcon />
                  </span>
                  {peopleCount} people
                </span>
              )}
              <div className="flex" aria-hidden="true">
                {visibleInitials.map((ch, i) => (
                  <span
                    key={`${ch}-${i}`}
                    className={`w-[22px] h-[22px] rounded-full border-2 border-[#F5F2FF] grid place-items-center text-[9px] text-white font-bold ${
                      PILE_BG[i % PILE_BG.length]
                    } ${i === 0 ? 'ml-0' : '-ml-2'}`}
                  >
                    {ch}
                  </span>
                ))}
                {overflowCount > 0 && (
                  <span className="w-[22px] h-[22px] rounded-full border-2 border-[#F5F2FF] grid place-items-center text-[9.5px] text-white font-bold bg-[#B5B0CC] -ml-2">
                    +{overflowCount}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Picker title */}
          <h2 className="mt-[18px] mx-1 mb-1 text-[17px] font-bold text-[#15131C]">
            Pick the times that work for you
          </h2>
          <p className="mx-1 mb-3 text-[13px] text-[#6B6880]">
            Select what works best. You can choose multiple options.
          </p>

          {/* Legend */}
          <div className="flex gap-3.5 flex-wrap mx-1 mb-3 text-[11.5px] text-[#6B6880]">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 rounded-full grid place-items-center text-white bg-[#1FB573]">
                <CheckIcon size={8} strokeWidth={3.5} />
              </span>
              Works
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 rounded-full grid place-items-center text-white bg-[#E9A23B]">
                <MaybeIcon size={8} />
              </span>
              Maybe / Could work
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 rounded-full grid place-items-center text-white bg-[#E04B5C]">
                <CantIcon size={8} />
              </span>
              Can&apos;t / Doesn&apos;t work
            </span>
          </div>

          {/* Day groups */}
          {days.map((day) => (
            <section
              key={day.dayId}
              className="bg-white border border-[#ECEAF1] rounded-[18px] px-3 pt-3 pb-1.5 shadow-[0_1px_2px_rgba(20,18,40,0.04)] mb-3"
            >
              <div className="flex items-center gap-2 px-1 pt-0.5 pb-2 border-b border-[#F1EEF7] mb-2">
                <div className="w-[26px] h-[26px] rounded-lg bg-[#F5F2FF] grid place-items-center text-[#5B4DE0]">
                  <CalendarIcon size={14} />
                </div>
                <div className="text-[13.5px] font-bold text-[#15131C]">
                  {day.dayLabel}
                </div>
              </div>

              {day.slots.map((slot, idx) => {
                const formatTime = (ms: number) =>
                  new Date(ms).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  });
                const timeLabel = `${formatTime(slot.startMs)} – ${formatTime(slot.endMs)}`;
                const current = responses[slot.id] ?? null;
                const isLast = idx === day.slots.length - 1;
                return (
                  <div
                    key={slot.id}
                    className={`grid grid-cols-[1fr_auto] gap-2 px-1 py-2 items-center ${
                      isLast ? '' : 'border-b border-[#F1EEF7]'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-semibold text-[#15131C]">
                        {timeLabel}
                      </div>
                      {slot.mealtag && (
                        <span className="text-[11px] text-[#6B6880] mt-px inline-flex items-center gap-1">
                          {slot.mealtag}
                        </span>
                      )}
                    </div>
                    <div
                      className="flex gap-1.5"
                      role="radiogroup"
                      aria-label={`${day.dayLabel} ${timeLabel} response`}
                    >
                      <SlotPill
                        state="works"
                        label="Works"
                        active={current === 'works'}
                        onClick={() => togglePill(slot.id, 'works')}
                      />
                      <SlotPill
                        state="maybe"
                        label="Maybe"
                        active={current === 'maybe'}
                        onClick={() => togglePill(slot.id, 'maybe')}
                      />
                      <SlotPill
                        state="cant"
                        label="Can't"
                        active={current === 'cant'}
                        onClick={() => togglePill(slot.id, 'cant')}
                      />
                    </div>
                  </div>
                );
              })}
            </section>
          ))}

          {/* Note field */}
          <label className="mt-1 bg-white border border-[#ECEAF1] rounded-[14px] px-3.5 py-3 flex items-start gap-2.5 shadow-[0_1px_2px_rgba(20,18,40,0.04)]">
            <span className="text-[#A6A3B8] mt-0.5 flex-shrink-0">
              <ChatIcon size={14} />
            </span>
            <span className="flex-1 block">
              <span className="block text-[12.5px] text-[#2C2840] font-semibold mb-0.5">
                Add a note (optional)
              </span>
              <textarea
                ref={textareaRef}
                rows={1}
                value={note}
                onChange={handleNoteInput}
                placeholder="e.g., Tuesday is best, or prefer earlier"
                className="w-full bg-transparent border-0 outline-none resize-none text-[12.5px] text-[#6B6880] p-0 min-h-[18px] font-inherit"
                style={{ maxHeight: 120 }}
              />
            </span>
          </label>

          {/* Promo */}
          <section
            aria-label="App promo"
            className="mt-3.5 border border-[#ECEAF1] rounded-[18px] p-3 grid grid-cols-[76px_1fr] gap-x-3 items-center shadow-[0_6px_18px_rgba(54,48,98,0.07)]"
            style={{ background: 'linear-gradient(180deg, #F4F1FF 0%, #FFFFFF 100%)' }}
          >
            <div className="w-[76px] rounded-xl overflow-hidden row-span-2">
              <img
                src={phoneMockup}
                alt="CatchUp app"
                loading="lazy"
                className="w-full h-auto block"
              />
            </div>
            <h2 className="m-0 mb-1.5 text-[13px] font-bold text-[#15131C] leading-snug">
              Make connections effortless with the{' '}
              <b className="text-[#5B4DE0]">CatchUp app</b>
            </h2>
            <ul className="m-0 mb-2 p-0 list-none grid gap-[3px]">
              <li className="text-[11.5px] text-[#6B6880] flex items-center gap-1.5">
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#5B4DE0] flex-shrink-0"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
                Smart nudges to reach out
              </li>
              <li className="text-[11.5px] text-[#6B6880] flex items-center gap-1.5">
                <span className="text-[#5B4DE0] flex-shrink-0">
                  <CheckIcon size={11} strokeWidth={2.4} />
                </span>
                One-tap invites &amp; instant scheduling
              </li>
            </ul>
            <div className="flex gap-2 items-center col-start-2">
              <button
                type="button"
                onClick={onDownloadApp}
                className="bg-[#5B4DE0] text-white border-0 rounded-[10px] px-3 py-[7px] text-[11.5px] font-bold active:translate-y-px"
              >
                Download CatchUp
              </button>
              <span className="text-[10.5px] text-[#6B6880] inline-flex items-center gap-1">
                <svg
                  width="9"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M16.5 2c.1 1.4-.5 2.7-1.4 3.6-.9 1-2.3 1.7-3.6 1.6-.1-1.4.5-2.7 1.4-3.7C13.7 2.6 15.2 2 16.5 2zM20 17.4c-.6 1.3-.9 1.9-1.7 3.1-1.1 1.6-2.6 3.6-4.5 3.6s-2.4-1.2-5-1.2-3.2 1.2-5 1.2-3.3-1.9-4.4-3.5C-.4 16.4-.7 11 1.5 8.1 3 6 5.4 4.7 7.7 4.7c2 0 3.5 1.2 5 1.2 1.5 0 2.6-1.2 5-1.2 1.8 0 3.7 1 5.1 2.6-4.4 2.4-3.7 8.7-2.8 10.1z" />
                </svg>
                Free on iOS
              </span>
            </div>
          </section>

          {/* SMS consent */}
          <section
            aria-label="Text message updates"
            className="mt-4 bg-white border border-[#ECEAF1] rounded-2xl p-3.5 shadow-[0_1px_2px_rgba(20,18,40,0.04)]"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-[9px] bg-[#F5F2FF] grid place-items-center text-[#5B4DE0]">
                <ChatIcon />
              </div>
              <div className="text-[13px] font-bold text-[#15131C]">
                Stay in the loop
                <small className="block font-medium text-[11.5px] text-[#6B6880] mt-px">
                  Confirmations, reschedules, reminders &mdash; keep coordination flowing.
                </small>
              </div>
            </div>

            <div className="flex items-start gap-2.5 py-1.5">
              <button
                type="button"
                role="checkbox"
                aria-checked={consented}
                aria-label="Consent to text updates"
                onClick={() => setConsented((v) => !v)}
                className={`w-5 h-5 rounded-md border-[1.5px] grid place-items-center flex-shrink-0 mt-px transition-all ${
                  consented
                    ? 'bg-[#5B4DE0] border-[#5B4DE0] text-white'
                    : 'bg-white border-[#A6A3B8] text-transparent'
                }`}
              >
                <CheckIcon size={12} strokeWidth={3.4} />
              </button>
              <div className="flex-1 text-[12.5px] text-[#2C2840] leading-snug">
                <b className="text-[#15131C]">
                  Text me at{' '}
                  <span className="text-[#5B4DE0] font-bold">
                    {smsConsent.maskedPhone}
                  </span>{' '}
                  with CatchUp AI updates
                </b>{' '}
                &mdash; confirmations, reschedules, reminders, and time-sensitive
                changes. Texts come from one CatchUp AI number, so you&apos;ll know it&apos;s us.
                Reply <b className="text-[#15131C]">STOP</b> to opt out anytime.
              </div>
            </div>
            <div className="mt-1 text-[10.5px] text-[#A6A3B8] leading-snug pl-[30px]">
              Msg &amp; data rates may apply. By checking this box you consent to
              receive transactional event-coordination texts from CatchUp AI,
              operated by CatchUp AI, at the number above.{' '}
              <a href="/privacy" className="text-[#6B6880] underline">Privacy</a> ·{' '}
              <a href="/terms" className="text-[#6B6880] underline">Terms</a>.
            </div>
          </section>

          {/* Submit */}
          <div className="relative mt-4">
            <button
              type="button"
              data-testid="submit-availability"
              onClick={handleSubmit}
              disabled={!hasAnyResponse}
              aria-disabled={!hasAnyResponse}
              className={`w-full rounded-[14px] py-3.5 text-[15px] font-bold flex items-center justify-center gap-2 transition active:translate-y-px ${
                hasAnyResponse
                  ? 'bg-[#5B4DE0] text-white shadow-[0_14px_28px_rgba(91,77,224,0.30)]'
                  : 'bg-[#A6A3B8] text-white cursor-not-allowed'
              }`}
            >
              Submit availability
              <ArrowRightIcon />
            </button>
          </div>

          <div className="mt-2.5 flex items-start gap-1.5 text-[11.5px] text-[#6B6880] leading-snug px-1">
            <span className="text-[#A6A3B8] flex-shrink-0 mt-px">
              <LockIcon size={13} />
            </span>
            Your response is private and only visible to the organizer.
          </div>

          <div className="mt-3.5 pt-3 border-t border-[#ECEAF1] flex justify-center items-center gap-2 text-[11px] text-[#6B6880]">
            <span className="text-[#A6A3B8] inline-flex">
              <LockIcon />
            </span>
            Secure
            <span className="w-[3px] h-[3px] bg-[#A6A3B8] rounded-full" />
            No account needed
            <span className="w-[3px] h-[3px] bg-[#A6A3B8] rounded-full" />
            Powered by <b className="text-[#5B4DE0] font-semibold">CatchUp</b>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AvailabilityPickerScreen;
